import { useState, useEffect, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';
import { SearchResultItem, EntityKind } from './types';

export function useHeaderSearch(query: string): {
  results: SearchResultItem[];
  totalCount: number;
  loading: boolean;
} {
  const searchApi = useApi(searchApiRef);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.trim().length < 1) {
      setResults([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchApi.query({
          term: query,
          types: ['software-catalog'],
        });

        if (cancelled) return;

        const mapped: SearchResultItem[] = response.results
          .slice(0, 8)
          .map(r => {
            const doc = r.document as unknown as Record<string, string>;
            const kind = (doc.kind || 'component').toLowerCase() as EntityKind;
            const name = doc.name || doc.title || '';
            const namespace = doc.namespace || 'default';
            const description = doc.description || doc.text || '';
            let link: string;
            if (kind === 'template') {
              link = `/create/templates/${namespace}/${name}`;
            } else {
              link = `/catalog/${namespace}/${kind}/${name}`;
            }
            return { kind, name, description, namespace, link };
          });

        setResults(mapped);
        setTotalCount(response.numberOfResults ?? response.results.length);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setResults([]);
          setTotalCount(0);
          setLoading(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, searchApi]);

  return { results, totalCount, loading };
}
